/**
 * Change Management API Service
 * Industry-standard Change Management System
 */

import axios from 'axios';
import { API_URL } from '../utils/ConfigUtils';

// Create axios instance with auth
const api = axios.create({
  baseURL: `${API_URL}/change-management`,
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface Team {
  id: string;
  name: string;
  code: string;
  description?: string;
  department?: string;
  team_lead?: string;
  manager?: string;
  members: string[];
  approvers: string[];
  management_approval_members?: string[];
  require_management_approval_for_high_risk?: boolean;
  default_change_category?: string;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface ApprovalStep {
  approver_id: string;
  approver_name?: string;
  role: 'team_lead' | 'manager' | 'management_approval' | 'security_officer' | 'admin';
  status: 'pending' | 'approved' | 'rejected' | 'skipped';
  comments?: string;
  decided_at?: string;
}

export interface ChangeHistory {
  action: string;
  performed_by: string;
  performed_by_name?: string;
  timestamp: string;
  old_status?: string;
  new_status?: string;
  details?: string;
  field_changes?: Record<string, { old: string; new: string }>;
}

export type ChangeType = 'normal' | 'standard' | 'emergency';
// Category code string (admin-managed). Examples: 'infrastructure', 'rule', 'custom_category_x'
export type ChangeCategory = string;
export type Priority = 'low' | 'medium' | 'high' | 'critical';
export type RiskLevel = 'low' | 'medium' | 'high' | 'critical';
export type ChangeStatus = 'draft' | 'submitted' | 'under_review' | 'approved' | 'rejected' | 'scheduled' | 'in_progress' | 'completed' | 'failed' | 'rolled_back' | 'cancelled' | 'closed';

export interface ChangeRequest {
  id: string;
  change_id: string;
  title: string;
  description: string;
  change_type: ChangeType;
  category: ChangeCategory;
  sub_category?: string;
  team: string;
  team_name?: string;
  team_department?: string;
  requested_by: string;
  requester_name?: string;
  assigned_to?: string;
  assignee_name?: string;
  regions: string[]; // List of region codes: BOM, SNG, RUH, AUH, JNB, Qatar
  tenant_id?: string;
  tenant_name?: string;
  priority: Priority;
  impact: Priority;
  urgency: Priority;
  risk_level: RiskLevel;
  risk_score: number;
  affected_systems: string[];
  affected_tenants: string[];
  affected_services: string[];
  configuration_items: any[];
  justification?: string;
  implementation_plan?: string;
  rollback_plan?: string;
  test_plan?: string;
  communication_plan?: string;
  scheduled_start?: string;
  scheduled_end?: string;
  estimated_duration?: number;
  maintenance_window: boolean;
  requires_downtime: boolean;
  downtime_duration?: number;
  status: ChangeStatus;
  approval_workflow: ApprovalStep[];
  current_approval_step: number;
  requires_management_approval?: boolean;
  template_id?: string;
  is_from_template: boolean;
  actual_start?: string;
  actual_end?: string;
  implementation_notes?: string;
  implemented_by?: string;
  implementer_name?: string;
  review_completed: boolean;
  success?: boolean;
  review_notes?: string;
  lessons_learned?: string;
  follow_up_required: boolean;
  follow_up_notes?: string;
  custom_field_values?: Record<string, any>; // Custom field values from template
  attachments: any[];
  related_incidents: string[];
  related_changes: string[];
  external_ticket?: string;
  history?: ChangeHistory[];
  created_at?: string;
  updated_at?: string;
  submitted_at?: string;
  approved_at?: string;
  closed_at?: string;
  can_approve?: boolean;
  can_reject?: boolean;
}

export interface TemplateCustomField {
  field_key: string;
  label: string;
  type: 'text' | 'number' | 'textarea' | 'select' | 'date' | 'boolean';
  is_required: boolean;
  options?: string[]; // For select type
  placeholder?: string;
  default_value?: string | number | boolean;
  description?: string;
}

export interface ChangeTemplate {
  id: string;
  template_name: string;
  description?: string;
  category: ChangeCategory;
  sub_category?: string;
  team: string;
  default_title_format?: string;
  implementation_plan?: string;
  rollback_plan?: string;
  test_plan?: string;
  risk_level: RiskLevel;
  estimated_duration?: number;
  requires_downtime: boolean;
  auto_approve: boolean;
  required_approvers: string[];
  custom_fields?: TemplateCustomField[];
  created_by?: string;
  created_by_name?: string;
  is_active: boolean;
  usage_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface ChangeCategoryDef {
  id: string;
  code: string;
  name: string;
  description?: string;
  order?: number;
  is_active: boolean;
}


export interface ChangeStatistics {
  by_status: Record<ChangeStatus, number>;
  by_category: Record<ChangeCategory, number>;
  by_risk_level: Record<RiskLevel, number>;
  by_type: Record<ChangeType, number>;
  success_rate: number;
  // Difference between last 7 days success rate and overall success rate (can be negative)
  success_rate_trend?: number;
  // Success rate for last 7 days only
  success_rate_7d?: number | null;
  recent_changes: number;
  pending_approvals: number;
  total_changes: number;
}

export interface PaginatedResponse<T> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// ============================================================================
// TEAM API
// ============================================================================

export const getTeams = async (includeInactive = false): Promise<Team[]> => {
  const response = await api.get('/teams', {
    params: { include_inactive: includeInactive }
  });
  return response.data.teams;
};

export const getTeam = async (teamId: string): Promise<Team> => {
  const response = await api.get(`/teams/${teamId}`);
  return response.data.team;
};

export const createTeam = async (team: Partial<Team>): Promise<Team> => {
  const response = await api.post('/teams', team);
  return response.data.team;
};

export const updateTeam = async (teamId: string, team: Partial<Team>): Promise<Team> => {
  const response = await api.put(`/teams/${teamId}`, team);
  return response.data.team;
};

// ============================================================================
// CHANGE CATEGORY API
// ============================================================================

export const getChangeCategories = async (
  activeOnly = true
): Promise<ChangeCategoryDef[]> => {
  const response = await api.get('/categories', {
    params: { active_only: activeOnly },
  });
  return response.data.categories;
};

export const createChangeCategory = async (
  payload: Partial<ChangeCategoryDef> & { name: string; code: string }
): Promise<ChangeCategoryDef> => {
  const response = await api.post('/categories', payload);
  return response.data.category;
};

export const updateChangeCategory = async (
  categoryId: string,
  payload: Partial<ChangeCategoryDef>
): Promise<ChangeCategoryDef> => {
  const response = await api.put(`/categories/${categoryId}`, payload);
  return response.data.category;
};

// ============================================================================
// CHANGE REQUEST API
// ============================================================================

export interface ChangeFilters {
  status?: string | string[];
  team?: string;
  category?: string;
  change_type?: string;
  risk_level?: string;
  requested_by?: string;
  assigned_to?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
  sort_by?: string;
}

export const getChanges = async (filters: ChangeFilters = {}): Promise<{
  changes: ChangeRequest[];
  pagination: { page: number; limit: number; total: number; pages: number };
}> => {
  const params: any = { ...filters };
  if (Array.isArray(filters.status)) {
    params.status = filters.status.join(',');
  }
  const response = await api.get('/changes', { params });
  return {
    changes: response.data.changes,
    pagination: response.data.pagination
  };
};

export const getChange = async (changeId: string, includeHistory = false): Promise<ChangeRequest> => {
  const response = await api.get(`/changes/${changeId}`, {
    params: { include_history: includeHistory }
  });
  return response.data.change;
};

export const createChange = async (change: Partial<ChangeRequest>): Promise<ChangeRequest> => {
  const response = await api.post('/changes', change);
  return response.data.change;
};

export const updateChange = async (changeId: string, change: Partial<ChangeRequest>): Promise<ChangeRequest> => {
  const response = await api.put(`/changes/${changeId}`, change);
  return response.data.change;
};

export const deleteChange = async (changeId: string): Promise<void> => {
  await api.delete(`/changes/${changeId}`);
};

// ============================================================================
// WORKFLOW ACTIONS
// ============================================================================

export const submitChange = async (changeId: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/submit`);
  return response.data.change;
};

export const approveChange = async (changeId: string, comments?: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/approve`, { comments });
  return response.data.change;
};

export const rejectChange = async (changeId: string, reason: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/reject`, { reason });
  return response.data.change;
};

export const scheduleChange = async (changeId: string, data: {
  scheduled_start: string;
  scheduled_end?: string;
  assigned_to?: string;
  assignee_name?: string;
}): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/schedule`, data);
  return response.data.change;
};

export const startChange = async (changeId: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/start`);
  return response.data.change;
};

export const completeChange = async (changeId: string, implementationNotes?: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/complete`, { implementation_notes: implementationNotes });
  return response.data.change;
};

export const failChange = async (changeId: string, reason: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/fail`, { reason });
  return response.data.change;
};

export const rollbackChange = async (changeId: string, rollbackNotes?: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/rollback`, { rollback_notes: rollbackNotes });
  return response.data.change;
};

export const closeChange = async (changeId: string, data: {
  review_notes?: string;
  lessons_learned?: string;
  follow_up_required?: boolean;
  follow_up_notes?: string;
}): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/close`, data);
  return response.data.change;
};

export const cancelChange = async (changeId: string, reason: string): Promise<ChangeRequest> => {
  const response = await api.post(`/changes/${changeId}/cancel`, { reason });
  return response.data.change;
};

// ============================================================================
// STATISTICS & REPORTS
// ============================================================================

export const getChangeStatistics = async (): Promise<ChangeStatistics> => {
  const response = await api.get('/changes/statistics');
  return response.data.statistics;
};

export const getPendingApprovals = async (): Promise<ChangeRequest[]> => {
  const response = await api.get('/changes/pending-approvals');
  return response.data.changes;
};

// ============================================================================
// TEMPLATES
// ============================================================================

export const getTemplates = async (filters: {
  team?: string;
  category?: string;
  active_only?: boolean;
} = {}): Promise<ChangeTemplate[]> => {
  const response = await api.get('/change-templates', { params: filters });
  return response.data.templates;
};

export const getTemplate = async (templateId: string): Promise<ChangeTemplate> => {
  const response = await api.get(`/change-templates/${templateId}`);
  return response.data.template;
};

export const createTemplate = async (template: Partial<ChangeTemplate>): Promise<ChangeTemplate> => {
  const response = await api.post('/change-templates', template);
  return response.data.template;
};

export const updateTemplate = async (templateId: string, template: Partial<ChangeTemplate>): Promise<ChangeTemplate> => {
  const response = await api.put(`/change-templates/${templateId}`, template);
  return response.data.template;
};

// ============================================================================

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const getStatusColor = (status: ChangeStatus): string => {
  const colors: Record<ChangeStatus, string> = {
    draft: 'bg-gray-500',
    submitted: 'bg-blue-500',
    under_review: 'bg-yellow-500',
    approved: 'bg-green-500',
    rejected: 'bg-red-500',
    scheduled: 'bg-purple-500',
    in_progress: 'bg-orange-500',
    completed: 'bg-emerald-500',
    failed: 'bg-red-600',
    rolled_back: 'bg-pink-500',
    cancelled: 'bg-gray-600',
    closed: 'bg-slate-500'
  };
  return colors[status] || 'bg-gray-500';
};

export const getStatusLabel = (status: ChangeStatus): string => {
  const labels: Record<ChangeStatus, string> = {
    draft: 'Draft',
    submitted: 'Submitted',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Rejected',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    failed: 'Failed',
    rolled_back: 'Rolled Back',
    cancelled: 'Cancelled',
    closed: 'Closed'
  };
  return labels[status] || status;
};

export const getRiskColor = (risk: RiskLevel): string => {
  const colors: Record<RiskLevel, string> = {
    low: 'bg-green-500',
    medium: 'bg-yellow-500',
    high: 'bg-orange-500',
    critical: 'bg-red-500'
  };
  return colors[risk] || 'bg-gray-500';
};

export const getPriorityColor = (priority: Priority): string => {
  const colors: Record<Priority, string> = {
    low: 'text-green-400',
    medium: 'text-yellow-400',
    high: 'text-orange-400',
    critical: 'text-red-400'
  };
  return colors[priority] || 'text-gray-400';
};

export const getCategoryIcon = (category: ChangeCategory): string => {
  const icons: Record<ChangeCategory, string> = {
    infrastructure: '🖥️',
    rule: '📋',
    integration: '🔗',
    security: '🔒',
    network: '🌐',
    application: '📱',
    configuration: '⚙️',
    other: '📦'
  };
  return icons[category] || '📦';
};

export const getTypeColor = (type: ChangeType): string => {
  const colors: Record<ChangeType, string> = {
    normal: 'bg-blue-500',
    standard: 'bg-green-500',
    emergency: 'bg-red-500'
  };
  return colors[type] || 'bg-gray-500';
};

// ============================================================================
// EXPORT UTILITIES
// ============================================================================

/**
 * Export a single change request as PDF
 */
export const exportChangeToPDF = (change: ChangeRequest): void => {
  // Create printable HTML content
  const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Change Request - ${change.change_id}</title>
      <style>
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { 
          font-family: 'Segoe UI', Arial, sans-serif; 
          margin: 0; 
          padding: 0;
          color: #1a1a1a; 
          background: #ffffff;
          line-height: 1.6;
        }
        .header { 
          background: linear-gradient(135deg, #426CFF 0%, #2C5FCC 100%);
          padding: 48px 40px 40px 40px;
          min-height: 170px;
          margin: 0 0 35px 0;
          border-radius: 0;
          box-shadow: 0 4px 12px rgba(66, 108, 255, 0.25);
          position: relative;
          overflow: visible;
        }
        .header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%);
          pointer-events: none;
        }
        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          position: relative;
          z-index: 1;
          width: 100%;
        }
        .header-left {
          flex: 1;
          min-width: 0;
        }
        .header-right {
          flex-shrink: 0;
          text-align: right;
          margin-left: 30px;
        }
        .badge-row {
          margin-bottom: 8px;
          display: flex;
          gap: 8px;
          justify-content: flex-end;
          flex-wrap: wrap;
        }
        .badge-row:last-child {
          margin-bottom: 0;
        }
        .header h1 { 
          color: #ffffff !important; 
          margin: 0 0 10px 0; 
          font-size: 32px; 
          line-height: 1.2;
          padding-top: 6px;
          display: block;
          font-weight: 700;
          text-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
          letter-spacing: -0.5px;
          position: relative;
          z-index: 1;
        }
        .header .subtitle { 
          color: #f0f5ff; 
          margin-top: 0; 
          font-size: 17px;
          font-weight: 400;
          opacity: 0.95;
          position: relative;
          z-index: 1;
        }
        .change-id-badge { 
          background: #ffffff; 
          padding: 12px 24px; 
          border-radius: 8px; 
          display: inline-block; 
          font-family: 'Courier New', 'Consolas', monospace; 
          color: #426CFF;
          font-weight: 700;
          font-size: 17px;
          box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
          letter-spacing: 1.2px;
          position: relative;
          z-index: 1;
          border: 1px solid rgba(66, 108, 255, 0.2);
        }
        .status-badge, .risk-badge {
          padding: 9px 18px;
          border-radius: 22px;
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          display: inline-block;
          box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
          position: relative;
          z-index: 1;
        }
        .status-badge.status-draft { background: #ffffff; color: #666666; }
        .status-badge.status-submitted, .status-badge.status-under_review { background: #fff3cd; color: #856404; }
        .status-badge.status-approved, .status-badge.status-scheduled { background: #d4edda; color: #155724; }
        .status-badge.status-in_progress { background: #cce5ff; color: #004085; }
        .status-badge.status-completed, .status-badge.status-closed { background: #28a745; color: #ffffff; }
        .status-badge.status-rejected, .status-badge.status-failed, .status-badge.status-cancelled { background: #f8d7da; color: #721c24; }
        .risk-badge.risk-low { background: #d4edda; color: #155724; }
        .risk-badge.risk-medium { background: #fff3cd; color: #856404; }
        .risk-badge.risk-high { background: #ffe0b2; color: #e65100; }
        .risk-badge.risk-critical { background: #f8d7da; color: #721c24; }
        .section { 
          margin-bottom: 30px; 
          page-break-inside: avoid;
        }
        .section h2 { 
          color: #426CFF !important; 
          font-size: 20px; 
          font-weight: 700;
          border-bottom: 3px solid #426CFF; 
          padding-bottom: 10px; 
          margin-bottom: 18px;
          letter-spacing: -0.3px;
        }
        .grid { 
          display: grid; 
          grid-template-columns: 1fr 1fr; 
          gap: 15px; 
        }
        .field { 
          margin-bottom: 12px; 
        }
        .field-label { 
          font-weight: 700; 
          color: #2c3e50 !important; 
          font-size: 12px; 
          text-transform: uppercase; 
          letter-spacing: 0.8px;
          margin-bottom: 6px;
          opacity: 0.85;
        }
        .field-value { 
          margin-top: 4px; 
          color: #1a1a1a !important;
          font-size: 15px;
          font-weight: 400;
        }
        .plan-content { 
          background: #f5f5f5; 
          padding: 15px; 
          border-radius: 8px; 
          white-space: pre-wrap; 
          font-family: 'Courier New', monospace; 
          font-size: 13px;
          color: #000000;
          border: 1px solid #ddd;
        }
        .tags { 
          display: flex; 
          flex-wrap: wrap; 
          gap: 8px; 
        }
        .tag { 
          background: #e0e0e0; 
          padding: 4px 10px; 
          border-radius: 4px; 
          font-size: 12px; 
          color: #000000;
        }
        .history-item { 
          padding: 12px; 
          border-left: 4px solid #426CFF; 
          background: #f9f9f9; 
          margin-bottom: 10px; 
          color: #000000;
        }
        .history-item strong {
          color: #000000 !important;
          font-weight: bold;
        }
        p { 
          color: #000000 !important; 
          margin-bottom: 10px;
        }
        .footer { 
          margin-top: 40px; 
          padding-top: 20px; 
          border-top: 1px solid #ddd; 
          font-size: 12px; 
          color: #666; 
          text-align: center; 
        }
        @media print { 
          body { margin: 20px; } 
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="header-content">
          <div class="header-left">
            <h1>Change Request Report</h1>
            <div class="subtitle">${(change.title || 'Untitled Change Request').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</div>
          </div>
          <div class="header-right">
            <div class="badge-row">
              <span class="change-id-badge">${change.change_id}</span>
            </div>
            <div class="badge-row">
              <span class="status-badge status-${change.status}">${change.status.replace(/_/g, ' ').toUpperCase()}</span>
              <span class="risk-badge risk-${change.risk_level}">${change.risk_level.toUpperCase()} RISK</span>
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Basic Information</h2>
        <div class="grid">
          <div class="field">
            <div class="field-label">Change Type</div>
            <div class="field-value">${change.change_type.charAt(0).toUpperCase() + change.change_type.slice(1)}</div>
          </div>
          <div class="field">
            <div class="field-label">Category</div>
            <div class="field-value">${change.category.charAt(0).toUpperCase() + change.category.slice(1)}</div>
          </div>
          <div class="field">
            <div class="field-label">Team</div>
            <div class="field-value">${change.team_name || change.team}</div>
          </div>
          <div class="field">
            <div class="field-label">Requester</div>
            <div class="field-value">${change.requester_name || 'N/A'}</div>
          </div>
          <div class="field">
            <div class="field-label">Created At</div>
            <div class="field-value">${new Date(change.created_at || '').toLocaleString()}</div>
          </div>
          <div class="field">
            <div class="field-label">External Ticket</div>
            <div class="field-value">${change.external_ticket || 'N/A'}</div>
          </div>
        </div>
      </div>

      <div class="section">
        <h2>Description</h2>
        <p>${change.description}</p>
      </div>

      <div class="section">
        <h2>Risk Assessment</h2>
        <div class="grid">
          <div class="field">
            <div class="field-label">Priority</div>
            <div class="field-value">${change.priority?.charAt(0).toUpperCase()}${change.priority?.slice(1) || 'N/A'}</div>
          </div>
          <div class="field">
            <div class="field-label">Impact</div>
            <div class="field-value">${change.impact?.charAt(0).toUpperCase()}${change.impact?.slice(1) || 'N/A'}</div>
          </div>
          <div class="field">
            <div class="field-label">Urgency</div>
            <div class="field-value">${change.urgency?.charAt(0).toUpperCase()}${change.urgency?.slice(1) || 'N/A'}</div>
          </div>
          <div class="field">
            <div class="field-label">Risk Level</div>
            <div class="field-value">${change.risk_level?.toUpperCase()}</div>
          </div>
        </div>
        ${change.justification ? `
        <div class="field" style="margin-top: 15px;">
          <div class="field-label">Business Justification</div>
          <div class="field-value">${change.justification}</div>
        </div>
        ` : ''}
      </div>

      ${change.affected_systems?.length || change.affected_services?.length ? `
      <div class="section">
        <h2>Affected Items</h2>
        ${change.affected_systems?.length ? `
        <div class="field">
          <div class="field-label">Affected Systems</div>
          <div class="tags" style="margin-top: 8px;">
            ${change.affected_systems.map(s => `<span class="tag">${s}</span>`).join('')}
          </div>
        </div>
        ` : ''}
        ${change.affected_services?.length ? `
        <div class="field" style="margin-top: 15px;">
          <div class="field-label">Affected Services</div>
          <div class="tags" style="margin-top: 8px;">
            ${change.affected_services.map(s => `<span class="tag">${s}</span>`).join('')}
          </div>
        </div>
        ` : ''}
      </div>
      ` : ''}

      ${change.implementation_plan ? `
      <div class="section">
        <h2>Implementation Plan</h2>
        <div class="plan-content">${change.implementation_plan}</div>
      </div>
      ` : ''}

      ${change.rollback_plan ? `
      <div class="section">
        <h2>Rollback Plan</h2>
        <div class="plan-content">${change.rollback_plan}</div>
      </div>
      ` : ''}

      ${change.test_plan ? `
      <div class="section">
        <h2>Test Plan</h2>
        <div class="plan-content">${change.test_plan}</div>
      </div>
      ` : ''}

      <div class="section">
        <h2>Scheduling</h2>
        <div class="grid">
          <div class="field">
            <div class="field-label">Scheduled Start</div>
            <div class="field-value">${change.scheduled_start ? new Date(change.scheduled_start).toLocaleString() : 'Not scheduled'}</div>
          </div>
          <div class="field">
            <div class="field-label">Scheduled End</div>
            <div class="field-value">${change.scheduled_end ? new Date(change.scheduled_end).toLocaleString() : 'Not scheduled'}</div>
          </div>
          <div class="field">
            <div class="field-label">Estimated Duration</div>
            <div class="field-value">${change.estimated_duration || 0} minutes</div>
          </div>
          <div class="field">
            <div class="field-label">Requires Downtime</div>
            <div class="field-value">${change.requires_downtime ? 'Yes' : 'No'}${change.requires_downtime && change.downtime_duration ? ` (${change.downtime_duration} min)` : ''}</div>
          </div>
        </div>
      </div>

      ${change.approval_workflow?.length ? `
      <div class="section">
        <h2>Approval History</h2>
        ${change.approval_workflow.map((step: ApprovalStep) => `
        <div class="history-item">
          <strong>${step.role.replace(/_/g, ' ').toUpperCase()}</strong> - ${step.status.toUpperCase()}
          ${step.comments ? `<br/><em>"${step.comments}"</em>` : ''}
          ${step.decided_at ? `<br/><small>Date: ${new Date(step.decided_at).toLocaleString()}</small>` : ''}
        </div>
        `).join('')}
      </div>
      ` : ''}

      <div class="footer">
        <p>Generated on ${new Date().toLocaleString()} | Eventus Security Change Management System</p>
      </div>
    </body>
    </html>
  `;

  // Extract styles and body content from HTML string
  const styleMatch = printContent.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
  const styles = styleMatch ? styleMatch[1] : '';
  const bodyMatch = printContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
  const bodyContent = bodyMatch ? bodyMatch[1] : printContent;

  // Create a unique ID for scoping styles
  const containerId = `pdf-container-${Date.now()}`;
  
  // Create a temporary container to render the HTML with styles
  const container = document.createElement('div');
  container.id = containerId;
  container.style.position = 'fixed';
  container.style.left = '-10000px';
  container.style.top = '0';
  container.style.width = '210mm';
  container.style.minHeight = '297mm';
  container.style.backgroundColor = '#ffffff';
  container.style.color = '#1a1a1a';
  container.style.fontFamily = "'Segoe UI', Arial, sans-serif";
  container.style.fontSize = '14px';
  container.style.lineHeight = '1.6';
  container.style.overflow = 'visible';
  
  // Create a scoped style element that only affects the container
  const styleElement = document.createElement('style');
  styleElement.id = `pdf-styles-${containerId}`;
  
  // Better CSS scoping - replace all selectors to be scoped to container
  let scopedStyles = styles;
  // Replace body, html, and root selectors
  scopedStyles = scopedStyles.replace(/^(\s*)body\s*\{/gm, `$1#${containerId}{`);
  scopedStyles = scopedStyles.replace(/^(\s*)\*\s*\{/gm, `$1#${containerId} *{`);
  // Replace class selectors (but not inside other selectors)
  scopedStyles = scopedStyles.replace(/(^|\n|\{)\s*\.([a-zA-Z0-9_-]+)/g, `$1#${containerId} .$2`);
  // Replace element selectors at the start of rules
  scopedStyles = scopedStyles.replace(/(^|\n|\{)\s*([a-zA-Z][a-zA-Z0-9]*)\s*\{/g, (match, prefix, selector) => {
    if (['html', 'body', '*'].includes(selector)) return match;
    return `${prefix}#${containerId} ${selector}{`;
  });
  
  styleElement.textContent = `
    #${containerId} {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    #${containerId} * {
      box-sizing: border-box;
    }
    ${scopedStyles}
    #${containerId} .header {
      background: linear-gradient(135deg, #426CFF 0%, #2C5FCC 100%) !important;
      padding: 48px 40px 40px 40px !important;
      min-height: 170px !important;
      margin: 0 !important;
      border-radius: 0 !important;
      box-shadow: 0 4px 12px rgba(66, 108, 255, 0.25) !important;
      position: relative !important;
      overflow: visible !important;
    }
    #${containerId} .header::before {
      content: '' !important;
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      right: 0 !important;
      bottom: 0 !important;
      background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 100%) !important;
      pointer-events: none !important;
    }
    #${containerId} .header h1 {
      color: #ffffff !important;
      font-size: 32px !important;
      line-height: 1.2 !important;
      padding-top: 6px !important;
      display: block !important;
      font-weight: 700 !important;
      text-shadow: 0 2px 6px rgba(0, 0, 0, 0.2) !important;
      letter-spacing: -0.5px !important;
      position: relative !important;
      z-index: 1 !important;
      margin: 0 0 10px 0 !important;
    }
    #${containerId} .header .subtitle {
      color: #f0f5ff !important;
      font-size: 17px !important;
      font-weight: 400 !important;
      opacity: 0.95 !important;
      position: relative !important;
      z-index: 1 !important;
      margin: 0 !important;
    }
    #${containerId} .change-id-badge {
      background: #ffffff !important;
      color: #426CFF !important;
      font-weight: 700 !important;
      padding: 12px 24px !important;
      border-radius: 8px !important;
      box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15) !important;
      position: relative !important;
      z-index: 1 !important;
      border: 1px solid rgba(66, 108, 255, 0.2) !important;
    }
    #${containerId} .status-badge,
    #${containerId} .risk-badge {
      font-weight: 700 !important;
      padding: 9px 18px !important;
      border-radius: 22px !important;
      box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2) !important;
      position: relative !important;
      z-index: 1 !important;
      display: inline-block !important;
    }
    #${containerId} .header-content {
      display: flex !important;
      justify-content: space-between !important;
      align-items: flex-start !important;
      position: relative !important;
      z-index: 1 !important;
      width: 100% !important;
    }
    #${containerId} .header-left {
      flex: 1 !important;
      min-width: 0 !important;
    }
    #${containerId} .header-right {
      flex-shrink: 0 !important;
      text-align: right !important;
      margin-left: 30px !important;
    }
    #${containerId} .badge-row {
      margin-bottom: 8px !important;
      display: flex !important;
      gap: 8px !important;
      justify-content: flex-end !important;
      flex-wrap: wrap !important;
    }
    #${containerId} .badge-row:last-child {
      margin-bottom: 0 !important;
    }
  `;
  document.head.appendChild(styleElement);
  
  // Wrap body content in a div with padding
  container.innerHTML = `<div style="padding: 20mm;">${bodyContent}</div>`;
  document.body.appendChild(container);

  // Use jsPDF and html2canvas to generate PDF
  Promise.all([
    import('jspdf'),
    import('html2canvas')
  ]).then(([jsPDFModule, html2canvasModule]) => {
    const { default: jsPDF } = jsPDFModule;
    const html2canvas = html2canvasModule.default;

    // Wait for content to render
    setTimeout(() => {
      html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        width: container.scrollWidth,
        height: container.scrollHeight
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/png');
        const pdf = new jsPDF('p', 'mm', 'a4');
        const imgWidth = 210; // A4 width in mm
        const pageHeight = 297; // A4 height in mm
        const imgHeight = (canvas.height * imgWidth) / canvas.width;
        let heightLeft = imgHeight;
        let position = 0;

        // Add first page
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        // Add additional pages if needed
        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }

        // Save the PDF
        pdf.save(`${change.change_id}.pdf`);
        
        // Clean up
        document.body.removeChild(container);
        document.head.removeChild(styleElement);
      }).catch((error: any) => {
        console.error('Error generating PDF:', error);
        document.body.removeChild(container);
        if (document.head.contains(styleElement)) {
          document.head.removeChild(styleElement);
        }
        // Fallback: use browser's print-to-PDF
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(printContent);
          printWindow.document.close();
          printWindow.focus();
          setTimeout(() => {
            printWindow.print();
          }, 250);
        }
      });
    }, 500);
  }).catch((error) => {
    console.error('Error loading PDF libraries:', error);
    document.body.removeChild(container);
    if (document.head.contains(styleElement)) {
      document.head.removeChild(styleElement);
    }
    // Fallback: use browser's print-to-PDF
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(printContent);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  });
};

/**
 * Export changes list to CSV
 */
export const exportChangesToCSV = (changes: ChangeRequest[], filename: string = 'change-requests'): void => {
  const headers = [
    'Change ID',
    'Title',
    'Status',
    'Type',
    'Category',
    'Team',
    'Requester',
    'Risk Level',
    'Priority',
    'Impact',
    'Urgency',
    'Scheduled Start',
    'Scheduled End',
    'Created At',
    'Updated At',
    'External Ticket'
  ];

  const rows = changes.map(change => [
    change.change_id,
    `"${(change.title || '').replace(/"/g, '""')}"`,
    change.status,
    change.change_type,
    change.category,
    change.team_name || change.team,
    change.requester_name || '',
    change.risk_level,
    change.priority,
    change.impact,
    change.urgency,
    change.scheduled_start ? new Date(change.scheduled_start).toLocaleString() : '',
    change.scheduled_end ? new Date(change.scheduled_end).toLocaleString() : '',
    change.created_at ? new Date(change.created_at).toLocaleString() : '',
    change.updated_at ? new Date(change.updated_at).toLocaleString() : '',
    change.external_ticket || ''
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
};

/**
 * Export changes list to Excel (XLSX format via CSV)
 */
export const exportChangesToExcel = (changes: ChangeRequest[], filename: string = 'change-requests'): void => {
  // For Excel, we use CSV with BOM for proper UTF-8 encoding
  const headers = [
    'Change ID',
    'Title',
    'Description',
    'Status',
    'Type',
    'Category',
    'Team',
    'Requester',
    'Risk Level',
    'Priority',
    'Impact',
    'Urgency',
    'Scheduled Start',
    'Scheduled End',
    'Est. Duration (min)',
    'Requires Downtime',
    'Created At',
    'Updated At',
    'External Ticket',
    'Affected Systems',
    'Affected Services'
  ];

  const rows = changes.map(change => [
    change.change_id,
    `"${(change.title || '').replace(/"/g, '""')}"`,
    `"${(change.description || '').replace(/"/g, '""').replace(/\n/g, ' ')}"`,
    change.status,
    change.change_type,
    change.category,
    change.team_name || change.team,
    change.requester_name || '',
    change.risk_level,
    change.priority,
    change.impact,
    change.urgency,
    change.scheduled_start ? new Date(change.scheduled_start).toLocaleString() : '',
    change.scheduled_end ? new Date(change.scheduled_end).toLocaleString() : '',
    change.estimated_duration || 0,
    change.requires_downtime ? 'Yes' : 'No',
    change.created_at ? new Date(change.created_at).toLocaleString() : '',
    change.updated_at ? new Date(change.updated_at).toLocaleString() : '',
    change.external_ticket || '',
    `"${(change.affected_systems || []).join(', ')}"`,
    `"${(change.affected_services || []).join(', ')}"`
  ]);

  // Add BOM for Excel UTF-8 compatibility
  const BOM = '\uFEFF';
  const csvContent = BOM + [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'application/vnd.ms-excel;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}-${new Date().toISOString().split('T')[0]}.xlsx`;
  link.click();
  URL.revokeObjectURL(link.href);
};
